import omit from 'lodash/omit'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import {
  ResumesDateFormatEnum,
  ResumesFontFamilyEnum,
  ResumesFontSizeEnum,
  ResumesFragment,
  ResumesHorizontalMarginEnum,
  ResumesLineHeightEnum,
  ResumesPageSizeEnum,
  ResumesProfilePictureShapeEnum,
  ResumesSectionTitleCasingEnum,
  ResumesTemplateColorEnum,
  ResumesTemplateEnum,
  ResumesVerticalMarginEnum,
} from '@gql/graphql'

import { defaultValues } from '@/forms/hooks/useResumeBuilderForm'
import { TResumeBuilderForm } from '@/forms/types'
import { TZoomScale } from '@/types/resume-builder'

export type TPublicResume = Pick<
  ResumesFragment,
  | 'aiGenerated'
  | 'archived'
  | 'certifications'
  | 'courseworks'
  | 'createdAt'
  | 'customSections'
  | 'documentType'
  | 'documentUrl'
  | 'educations'
  | 'firstName'
  | 'groupedSkills'
  | 'id'
  | 'involvements'
  | 'isPublic'
  | 'lastName'
  | 'name'
  | 'preferences'
  | 'projects'
  | 'publicId'
  | 'showOriginalDocument'
  | 'simpleAnalysis'
  | 'suggestedSkills'
  | 'template'
  | 'templateColorEnum'
  | 'uniqueId'
  | 'updatedAt'
  | 'workExperiences'
> &
  TResumeBuilderForm

interface IUseResumeBuilderStoreId {
  clearResumeId: () => void
  resumeId?: string
  setResumeId: (resumeId: string) => void
}

interface IUseResumeBuilderStoreTour {
  setTourCompleted: (tourCompleted: boolean) => void
  tourCompleted: boolean
}

interface IUseResumeBuilderSettingsStore {
  setZoom: (zoom: TZoomScale) => void
  zoom: TZoomScale
}

export const resumeInitialValues: TPublicResume = {
  ...defaultValues,
  aiGenerated: false,
  archived: false,
  certifications: [],
  courseworks: [],
  createdAt: '',
  customSections: [],
  educations: [],
  firstName: '',
  groupedSkills: [],
  id: '',
  involvements: [],
  isPublic: false,
  lastName: '',
  name: '',
  preferences: {
    avatar: {
      profilePictureEnabled: false,
      profilePictureShape: ResumesProfilePictureShapeEnum.Square,
    },
    dateFormat: ResumesDateFormatEnum.ShortName,
    fontFamily: ResumesFontFamilyEnum.Garamond,
    fontSize: ResumesFontSizeEnum.Standard,
    hideCertifications: false,
    hideCourseworks: false,
    hideEducations: false,
    hideInvolvements: false,
    hideProjects: false,
    hideSkills: false,
    hideSummary: false,
    hideTargetTitle: false,
    hideWorkExperiences: false,
    horizontalMargin: ResumesHorizontalMarginEnum.Standard,
    lineHeight: ResumesLineHeightEnum.Standard,
    pageSize: ResumesPageSizeEnum.A4,
    sectionTitleCasing: ResumesSectionTitleCasingEnum.Capitalized,
    sectionsOrder: [],
    sectionsOrderWithCustomSections: [],
    template: ResumesTemplateEnum.Harvard,
    templateColorEnum: ResumesTemplateColorEnum.HarvardLight,
    verticalMargin: ResumesVerticalMarginEnum.Standard,
  },
  projects: [],
  publicId: '',
  showOriginalDocument: false,
  simpleAnalysis: [],
  suggestedSkills: [],
  template: ResumesTemplateEnum.Harvard,
  templateColorEnum: ResumesTemplateColorEnum.HarvardLight,
  uniqueId: '',
  updatedAt: '',
  workExperiences: [],
}

export const useResumeBuilderStoreId = create<IUseResumeBuilderStoreId>()(
  persist(
    (set) => ({
      clearResumeId: () => set((state) => omit(state, ['resumeId']), true),
      resumeId: undefined,
      setResumeId: (newResumeId) => set((state) => ({ resumeId: newResumeId })),
    }),
    {
      name: 'resume-builder-id',
      storage: createJSONStorage(() => localStorage),
    }
  )
)

export const useResumeBuilderStoreTour = create<IUseResumeBuilderStoreTour>()(
  persist(
    (set) => ({
      setTourCompleted: (tourCompleted) => set((state) => ({ tourCompleted })),
      tourCompleted: false,
    }),
    {
      name: 'resume-builder-tour',
      storage: createJSONStorage(() => localStorage),
    }
  )
)

export const useResumeBuilderStoreSettings = create<IUseResumeBuilderSettingsStore>()(
  persist(
    (set) => ({
      setZoom: (zoom) => set((state) => ({ zoom })),
      zoom: 1,
    }),
    {
      name: 'resume-builder-settings',
      storage: createJSONStorage(() => localStorage),
    }
  )
)
